import { useContext, useEffect, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { loadState, saveState } from "../../utils/SaveStateUtil";
import { useAuthStatus } from "../../context/AuthContext";
import { MapContext } from "../../context/MapContext";

// 範囲ダウンロードの1辺入力の仕様（m）
export const CHIBAN_POLYGON_DL_SIDE_DEFAULT = 100;
export const CHIBAN_POLYGON_DL_SIDE_MIN = 1;
export const CHIBAN_POLYGON_DL_SIDE_MAX = 200;
export const CHIBAN_POLYGON_DL_SIDE_STEP = 1;

/**
 * 地番ポリゴンダウンロードダイアログ
 * 表示と入力のみを担い、抽出・保存処理は呼び出し側（RightSide）が行う
 * @param {boolean} show 表示状態
 * @param {Function} handleClose 閉じる操作
 * @param {string} targetLabel 対象筆の表示文字列（住所＋ファイル名）
 * @param {string} singleFilename 単筆モードのデフォルトファイル名（表示用）
 * @param {string} rangeFilename 範囲モードのデフォルトファイル名（表示用）
 * @param {Function} onExecute 実行。(mode, sideMeters) => boolean（true で閉じる／false で開いたまま）
 */
const ChibanPolygonDlModal = ({
  show,
  handleClose,
  targetLabel,
  singleFilename,
  rangeFilename,
  onExecute,
}) => {
  const [mode, setMode] = useState("single");
  const [sideMeters, setSideMeters] = useState(CHIBAN_POLYGON_DL_SIDE_DEFAULT);
  const { isCheckSaveState } = useContext(MapContext);
  const { isLoading, isUserLoggedIn } = useAuthStatus();
  const isEnabledSaveStateRef = useRef(false);
  const isFirstLoadRef = useRef(true);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    // 初期表示の状態読込
    if (isFirstLoadRef.current) {
      if (isUserLoggedIn) {
        const checkMapState = loadState("checkMapState");
        const chibanPolygonDlSetting = loadState("chibanPolygonDlSetting");
        // 初回は、保存データから再現ONか読む（以降はpropから取得）
        if (checkMapState?.isCheckSaveState && chibanPolygonDlSetting) {
          setMode(chibanPolygonDlSetting.mode);
          setSideMeters(chibanPolygonDlSetting.sideMeters);
        }
      }
      isFirstLoadRef.current = false;
    }
    // 設定保存可否
    isEnabledSaveStateRef.current = isUserLoggedIn && isCheckSaveState;
  }, [isCheckSaveState, isLoading, isUserLoggedIn]);

  // 設定保存
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (isEnabledSaveStateRef.current) {
        saveState("chibanPolygonDlSetting", { mode, sideMeters });
      }
    }, 500); // デバウンスで遅延させる
    return () => {
      clearTimeout(debounce);
    };
  }, [isCheckSaveState, mode, sideMeters]);

  const handleDownload = () => {
    // 成功時のみ閉じる（画面外・0件時は開いたまま再操作できるようにする）
    if (onExecute(mode, Number(sideMeters))) {
      handleClose();
    }
  };

  // 直接入力はスピナーと違い min/max が効かないため、フォーカスアウト時に欄の値も仕様範囲へ丸める
  // （実行時のクランプは安全網として残す）
  const clampSideMeters = () => {
    const num = Number(sideMeters);
    if (sideMeters === "" || !Number.isFinite(num)) {
      setSideMeters(CHIBAN_POLYGON_DL_SIDE_DEFAULT);
      return;
    }
    setSideMeters(
      Math.min(
        Math.max(num, CHIBAN_POLYGON_DL_SIDE_MIN),
        CHIBAN_POLYGON_DL_SIDE_MAX
      )
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">地番ポリゴンダウンロード</Modal.Title>
      </Modal.Header>
      <Modal.Body className="mx-4">
        <Form.Group className="mb-3">
          <Form.Label className="mb-0">対象</Form.Label>
          <div>{targetLabel}</div>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>出力範囲</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              id="chiban-polygon-dl-mode-single"
              name="chiban-polygon-dl-mode"
              label="該当の地番のみ"
              checked={mode === "single"}
              onChange={() => setMode("single")}
            />
            <Form.Check
              type="radio"
              id="chiban-polygon-dl-mode-range"
              name="chiban-polygon-dl-mode"
              label="一定の範囲"
              checked={mode === "range"}
              onChange={() => setMode("range")}
            />
          </div>
          {/* 単筆選択時は範囲設定ブロック全体を非活性の色にする */}
          <div className={`mx-4 ${mode !== "range" ? "text-muted" : ""}`}>
            <div className="mt-2">クリック地点を中心とした矩形範囲</div>
            <div className="d-flex align-items-center gap-2 mt-2">
              <Form.Label className="mb-0">1辺</Form.Label>
              <Form.Control
                type="number"
                min={CHIBAN_POLYGON_DL_SIDE_MIN}
                max={CHIBAN_POLYGON_DL_SIDE_MAX}
                step={CHIBAN_POLYGON_DL_SIDE_STEP}
                disabled={mode !== "range"}
                value={sideMeters}
                onChange={(e) => setSideMeters(e.target.value)}
                onBlur={clampSideMeters}
                style={{ width: "100px" }}
              />
              <Form.Label className="mb-0">m</Form.Label>
            </div>
          </div>
        </Form.Group>
        <Form.Group>
          <Form.Label className="mb-0">ファイル名</Form.Label>
          <div style={{ wordBreak: "break-all" }}>
            {mode === "single" ? singleFilename : rangeFilename}
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={handleClose}>
          閉じる
        </Button>
        <div className="flex-fill" />
        <Button variant="primary" onClick={handleDownload}>
          ダウンロード
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChibanPolygonDlModal;
